import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Shuffle } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { cn } from '../../../../lib/utils';
import { MAX_BRACKET_PLAYERS } from '../../../../lib/bracket';
import { minParticipants } from '../../../../lib/stages';
import { startStage } from '../../../../database/tournamentStages';
import { handleFirebaseError } from '../../../../utils/errorHandler';

// StageManager only mounts this once a stage is selected, so `stage` is
// always defined here.
const StartStageDialog = ({
  open,
  onOpenChange,
  tournamentId,
  stage,
  index,
  players,
  playersById,
}) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let list;
    if (index === 0) {
      const stored = stage.participants || [];
      if (stored.length) {
        const storedSet = new Set(stored);
        const remaining = players.filter((p) => !storedSet.has(p.id));
        list = [
          ...stored.map((id) => ({ id, included: true })),
          ...remaining.map((p) => ({ id: p.id, included: false })),
        ];
      } else {
        list = [...players]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((p) => ({ id: p.id, included: true }));
      }
    } else {
      list = (stage.participants || []).map((id) => ({ id, included: true }));
    }
    setRows(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stage.id, index]);

  const isBracket = stage.type === 'bracket';
  const min = minParticipants(stage);
  const includedCount = (rows || []).filter((r) => r.included).length;
  const error =
    includedCount < min
      ? `Pick at least ${min} players.`
      : isBracket && includedCount > MAX_BRACKET_PLAYERS
        ? `A bracket can have at most ${MAX_BRACKET_PLAYERS} players.`
        : null;

  const toggleIncluded = (id) => {
    setRows((prevRows) => {
      const row = prevRows.find((r) => r.id === id);
      const rest = prevRows.filter((r) => r.id !== id);
      const updated = { ...row, included: !row.included };
      const includedRows = rest.filter((r) => r.included);
      const excludedRows = rest.filter((r) => !r.included);
      return updated.included
        ? [...includedRows, updated, ...excludedRows]
        : [...includedRows, ...excludedRows, updated];
    });
  };

  const move = (id, direction) => {
    setRows((prevRows) => {
      const i = prevRows.findIndex((r) => r.id === id);
      const j = i + direction;
      if (j < 0 || j >= prevRows.length || !prevRows[j].included) return prevRows;
      const next = [...prevRows];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const shuffle = () => {
    setRows((prevRows) => {
      const included = prevRows.filter((r) => r.included);
      const excluded = prevRows.filter((r) => !r.included);
      for (let i = included.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [included[i], included[j]] = [included[j], included[i]];
      }
      return [...included, ...excluded];
    });
  };

  const selectAll = () => setRows((prevRows) => prevRows.map((r) => ({ ...r, included: true })));
  const selectNone = () => setRows((prevRows) => prevRows.map((r) => ({ ...r, included: false })));

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const ids = rows.filter((r) => r.included).map((r) => r.id);
      await startStage(tournamentId, stage.id, ids);
      onOpenChange(false);
    } catch (err) {
      toast.error('Could not start the stage.', { description: handleFirebaseError(err) });
    } finally {
      setBusy(false);
    }
  };

  let seed = 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {rows && (
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Start {stage.name}</DialogTitle>
          </DialogHeader>

          <p className="text-sm text-muted-foreground">
            Pick who plays{isBracket ? ' and set the seeds' : ''}. Seed 1 meets the lowest seed
            first.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={selectAll}>
              Select all
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={selectNone}>
              Select none
            </Button>
            {isBracket && (
              <Button type="button" variant="ghost" size="sm" onClick={shuffle}>
                <Shuffle className="h-3.5 w-3.5" />
                Shuffle seeds
              </Button>
            )}
          </div>

          <ul className="max-h-80 overflow-y-auto">
            {rows.map((r) => {
              const player = playersById[r.id];
              if (r.included) seed += 1;
              const mySeed = r.included ? seed : null;
              return (
                <li
                  key={r.id}
                  className={cn(
                    'flex items-center gap-3 border-b border-border/40 py-2',
                    !r.included && 'opacity-50'
                  )}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-heat"
                    checked={r.included}
                    aria-label={`Include ${player?.name || 'player'}`}
                    onChange={() => toggleIncluded(r.id)}
                  />
                  {isBracket && (
                    <>
                      <span className="w-6 font-mono text-xs tabular-nums text-muted-foreground">
                        {mySeed ?? ''}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Move ${player?.name || 'player'} up`}
                        disabled={!r.included}
                        onClick={() => move(r.id, -1)}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Move ${player?.name || 'player'} down`}
                        disabled={!r.included}
                        onClick={() => move(r.id, 1)}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    {player?.name || 'Removed player'}
                  </span>
                </li>
              );
            })}
          </ul>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="button" disabled={!!error || busy} onClick={handleConfirm}>
              {busy ? 'Starting…' : `Start ${stage.name}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default StartStageDialog;
